import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMe } from './vendorsApi';
import { atLeast, levelForTags } from './vendorLabels';

const NO_LEVELS = [];

/**
 * The caller's vendor access (GET /api/vendors/me). Drives which tabs, buttons
 * and filters render. A failed read sets `error` and leaves `me` null: callers
 * show a full error, never an empty "no access" state.
 */
export default function useVendorAccess() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getMe();
      if (!mounted.current) return;
      setMe(data || null);
      if (!data) setError(true);
    } catch {
      if (!mounted.current) return;
      setMe(null);
      setError(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const levels = me?.levels || NO_LEVELS;
  const isAdmin = !!me?.is_admin;
  const isOwner = isAdmin || (me?.owner_tag_ids || []).length > 0;

  // Highest level over the given speciality ids (a vendor's tags). Admins are
  // approve everywhere; see-all alone is view.
  const levelFor = useCallback((tagIds) => levelForTags(me, tagIds), [me]);

  // Does the caller hold at least `min` on any speciality?
  const hasLevelAtLeast = useCallback(
    (min) => isAdmin || levels.some((l) => atLeast(l.level, min)),
    [isAdmin, levels],
  );

  const hasAccess = !!me && (isAdmin || !!me.see_all || !!me.triage || levels.length > 0 || isOwner);

  return useMemo(
    () => ({ loading, error, me, levelFor, isOwner, hasAccess, hasLevelAtLeast, refresh }),
    [loading, error, me, levelFor, isOwner, hasAccess, hasLevelAtLeast, refresh],
  );
}
